import { mdiAlertCircle, mdiCheckCircle } from '@mdi/js';
import * as React from 'react';
import { GroupBase } from 'react-select';
import { CreatableProps } from 'react-select/creatable';
import { validateLatin1String } from '../../../utils';
import { ISelectOption } from '../select';
import * as S from './input.styles';

const components = {
    DropdownIndicator: null,
    ClearIndicator: () => null,
};

export type IBaseCreatableInputProps = Omit<CreatableProps<string, boolean, GroupBase<string>>, 'onChange'> & {
    successMessage?: string;
    errorMessage?: string;
    value?: string[];
    disabled?: boolean;
    onChange?: (value: string[]) => void;
    disableLatin1Validation?: boolean;
};

const createOption = (label: string) => ({
    label,
    value: label,
});

export const CreatableInput = (props: IBaseCreatableInputProps) => {
    const { successMessage, errorMessage, disabled, disableLatin1Validation, ...otherProps } = props;
    const { value, onChange } = otherProps;

    const [inputValue, setInputValue] = React.useState<string>('');
    const [latin1ValidationError, setLatin1ValidationError] = React.useState<string | null>(null);

    const handleChange = (option: unknown) => {
        if (option) {
            const selectedOption = option as ISelectOption<string>[];

            onChange && onChange((selectedOption || []).map(v => v.label.toString()));
        }
    };

    const handleInputChange = (newValue: string) => {
        setInputValue(newValue);
        if (latin1ValidationError) {
            setLatin1ValidationError(null);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!disableLatin1Validation && inputValue) {
            const validationResult = validateLatin1String(inputValue);
            if (!validationResult.isValid) {
                setLatin1ValidationError(validationResult.errorMessage || null);
            }
        }
        
        addNewValue(inputValue);
        props.onBlur && props.onBlur(e);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        switch (event.key) {
            case 'Enter':
            case 'Tab':
                addNewValue(inputValue);
                event.preventDefault();
                break;
            default:
                break;
        }
    };

    const addNewValue = (val: string) => {
        if (!val) {
            return;
        }

        setInputValue('');
        const newValue = [...(value || []), val];

        onChange && onChange(newValue);
    };

    const mappedValue = (value || []).map(v => createOption(v));

    const displayError = errorMessage || latin1ValidationError;

    return (
        <S.InputContainer>
            <S.InputWrapper>
                <S.CreatableInput
                    {...(otherProps as any)}
                    components={components}
                    inputValue={inputValue}
                    isClearable={true}
                    isDisabled={disabled}
                    isMulti={true}
                    menuIsOpen={false}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    value={mappedValue}
                    classNamePrefix="select"
                    onBlur={handleBlur}
                />
            </S.InputWrapper>

            {successMessage && (
                <S.InputBottomSuccess>
                    <S.InputBottomIcon path={mdiCheckCircle} />
                    <S.InputBottomMessage>{successMessage}</S.InputBottomMessage>
                </S.InputBottomSuccess>
            )}

            {displayError && (
                <S.InputBottomError>
                    <S.InputBottomIcon path={mdiAlertCircle} />
                    <S.InputBottomMessage>{displayError}</S.InputBottomMessage>
                </S.InputBottomError>
            )}
        </S.InputContainer>
    );
};
